import { filter, forEach, map, pipe } from 'remeda'
import type { ViewHook } from '../helpers/utils'

type Intent = 'print' | 'save'

type ItemLabelPdfController = Readonly<{
  destroy: () => void
  disconnect: () => void
  reconnect: () => void
  updated: () => void
}>

type ItemLabelPdfHookContext = ViewHook & {
  _itemLabelPdfController?: ItemLabelPdfController
}

type ErrorPayload = Readonly<{
  error: string
}>

type ItemLabelPdfMessages = Readonly<{
  frameTitle: string
  generationFailed: string
  pdfRequestFailed: string
  printFallback: string
  previewFailed: string
}>

type RenderedPreviewState =
  | Readonly<{ status: 'idle' }>
  | Readonly<{ currentUrl: string | undefined; status: 'loading' }>
  | Readonly<{ status: 'ready'; url: string }>
  | Readonly<{
      currentUrl: string | undefined
      message: string
      status: 'error'
    }>

const PDF_BUSY_RETRY_LIMIT = 8
const MAX_PDF_BUSY_RETRY_DELAY_MS = 5_000
const activePdfRequests = new WeakMap<HTMLFormElement, AbortController>()

const isRecord = (value: unknown): value is Readonly<Record<string, unknown>> =>
  typeof value === 'object' && value !== null

const isErrorPayload = (value: unknown): value is ErrorPayload =>
  isRecord(value) && typeof value.error === 'string'

const messagesFrom = (form: HTMLFormElement): ItemLabelPdfMessages => ({
  frameTitle: form.dataset.labelFrameTitle ?? 'Item labels print view',
  generationFailed:
    form.dataset.labelGenerationFailed ??
    'The label PDF could not be generated.',
  pdfRequestFailed:
    form.dataset.labelPdfRequestFailed ?? 'PDF request failed (__STATUS__).',
  printFallback:
    form.dataset.labelPrintFallback ??
    'The browser could not open the print dialog. Open the print view and print at Actual size / 100%.',
  previewFailed:
    form.dataset.labelPreviewFailed ??
    'The label preview could not be generated.'
})

const isLabelSubmitButton = (element: Element): element is HTMLButtonElement =>
  element instanceof HTMLButtonElement &&
  element.dataset.labelSubmit !== undefined

const submitButtons = (form: HTMLFormElement): readonly HTMLButtonElement[] =>
  pipe(Array.from(form.elements), filter(isLabelSubmitButton))

const forEachSubmitButton = (
  form: HTMLFormElement,
  effect: (button: HTMLButtonElement) => void
): void => {
  pipe(submitButtons(form), forEach(effect))
}

const setMessage = (form: HTMLFormElement, message?: string): void => {
  const element = form.querySelector<HTMLElement>('[data-label-error]')

  if (element) {
    element.textContent = message ?? ''
    element.hidden = !message
  }
}

const setPdfFallback = (
  form: HTMLFormElement,
  url?: string
): HTMLAnchorElement | undefined => {
  const element =
    form.querySelector<HTMLAnchorElement>('[data-label-pdf-fallback]') ??
    undefined

  if (element) {
    element.hidden = !url
    element.removeAttribute('href')

    if (url) {
      element.href = url
    }
  }

  return element
}

const previewUrlFrom = (form: HTMLFormElement): string | undefined =>
  form.dataset.labelPreviewReady === 'true'
    ? form.dataset.labelPreviewUrl
    : undefined

const previewDelayFrom = (form: HTMLFormElement): number => {
  const parsed = Number.parseInt(form.dataset.labelPreviewDelay ?? '500', 10)
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 500
}

const pdfBusyRetryFallbackDelayFrom = (form: HTMLFormElement): number => {
  const parsed = Number.parseInt(
    form.dataset.labelPdfBusyRetryDelay ?? '500',
    10
  )
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 500
}

const pdfBusyRetryDelayFrom = (
  form: HTMLFormElement,
  response: Response
): number => {
  const retryAfter = response.headers.get('retry-after')
  const seconds = retryAfter === null ? NaN : Number(retryAfter)

  return Number.isFinite(seconds) && seconds >= 0
    ? Math.min(seconds * 1_000, MAX_PDF_BUSY_RETRY_DELAY_MS)
    : pdfBusyRetryFallbackDelayFrom(form)
}

const abortableDelay = (delay: number, signal: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException('Aborted', 'AbortError'))
      return
    }

    const abort = (): void => {
      window.clearTimeout(timer)
      signal.removeEventListener('abort', abort)
      reject(new DOMException('Aborted', 'AbortError'))
    }
    const timer = window.setTimeout(() => {
      signal.removeEventListener('abort', abort)
      resolve()
    }, delay)

    signal.addEventListener('abort', abort, { once: true })
  })

const previewValue = (formData: FormData, name: string): number => {
  const value = formData.get(name)
  const parsed = typeof value === 'string' ? Number.parseFloat(value) : NaN

  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1
}

const updatePreviewDimensions = (form: HTMLFormElement): void => {
  const canvas = form.querySelector<HTMLElement>('[data-label-preview-canvas]')

  if (!canvas) {
    return
  }

  const formData = new FormData(form)
  const width = previewValue(formData, 'label[width_mm]')
  const height = previewValue(formData, 'label[height_mm]')
  const ratio = width / height
  canvas.style.aspectRatio = `${width} / ${height}`
  canvas.style.width = `${Math.min(440, 288 * ratio)}px`
}

const renderPreviewState = (
  form: HTMLFormElement,
  state: RenderedPreviewState
): void => {
  const canvas = form.querySelector<HTMLElement>('[data-label-preview-canvas]')
  const image = form.querySelector<HTMLImageElement>(
    '[data-label-preview-image]'
  )
  const loading = form.querySelector<HTMLElement>(
    '[data-label-preview-loading]'
  )
  const error = form.querySelector<HTMLElement>('[data-label-preview-error]')
  const currentUrl =
    state.status === 'ready'
      ? state.url
      : state.status === 'loading' || state.status === 'error'
        ? state.currentUrl
        : undefined

  if (canvas) {
    canvas.setAttribute('aria-busy', String(state.status === 'loading'))
  }

  if (image) {
    image.hidden = !currentUrl

    if (currentUrl) {
      image.src = currentUrl
    } else {
      image.removeAttribute('src')
    }
  }

  if (loading) {
    loading.hidden = state.status !== 'loading'
  }

  if (error) {
    error.hidden = state.status !== 'error'

    if (state.status === 'error') {
      error.textContent = state.message
    }
  }
}

const intentFromSubmitter = (submitter: HTMLElement | null): Intent =>
  submitter instanceof HTMLButtonElement &&
  submitter.dataset.labelIntent === 'print'
    ? 'print'
    : 'save'

const fileNameFrom = (response: Response): string => {
  const disposition = response.headers.get('content-disposition') ?? ''
  const match = /filename="([^"]+)"/i.exec(disposition)

  return match?.[1] ?? 'item-labels.pdf'
}

const responseError = async (
  response: Response,
  fallbackMessage: string
): Promise<string> => {
  try {
    const payload: unknown = await response.json()

    return isErrorPayload(payload)
      ? payload.error
      : fallbackMessage.replace('__STATUS__', String(response.status))
  } catch {
    return fallbackMessage.replace('__STATUS__', String(response.status))
  }
}

const downloadPdf = (url: string, fileName: string): void => {
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  link.hidden = true
  document.body.append(link)
  link.click()
  link.remove()
}

const decodePrintImages = async (printWindow: Window): Promise<void> => {
  const decodes = pipe(
    Array.from(printWindow.document.querySelectorAll<HTMLImageElement>('img')),
    map((image) =>
      typeof image.decode === 'function'
        ? image.decode().catch(() => undefined)
        : Promise.resolve()
    )
  )

  await Promise.all(decodes)
}

const setupItemLabelPdf = (form: HTMLFormElement): ItemLabelPdfController => {
  const messages = messagesFrom(form)
  const lifecycleController = new AbortController()
  const actionRoot = form.closest<HTMLElement>('wa-drawer') ?? form
  let requestController: AbortController | undefined
  let previewController: AbortController | undefined
  let previewTimer: number | undefined
  let previewObjectUrl: string | undefined
  let printController: AbortController | undefined
  let objectUrl: string | undefined
  let printFrame: HTMLIFrameElement | undefined
  let timers: readonly number[] = []
  let disconnected = false

  const rememberTimer = (timer: number): void => {
    timers = [...timers, timer]
  }

  const clearArtifacts = (): void => {
    pipe(
      timers,
      forEach((timer) => window.clearTimeout(timer))
    )
    timers = []
    printController?.abort()
    printController = undefined
    printFrame?.remove()
    printFrame = undefined

    if (objectUrl) {
      URL.revokeObjectURL(objectUrl)
      objectUrl = undefined
    }

    setPdfFallback(form)
  }

  const clearPreviewTimer = (): void => {
    if (previewTimer !== undefined) {
      window.clearTimeout(previewTimer)
      previewTimer = undefined
    }
  }

  const cancelPreviewRequest = (): void => {
    clearPreviewTimer()
    previewController?.abort()
    previewController = undefined
  }

  const cancelPdfRequest = (): void => {
    const controller = requestController
    requestController = undefined
    controller?.abort()

    if (controller && activePdfRequests.get(form) === controller) {
      activePdfRequests.delete(form)
    }
  }

  const clearRenderedPreview = (): void => {
    cancelPreviewRequest()

    if (previewObjectUrl) {
      URL.revokeObjectURL(previewObjectUrl)
      previewObjectUrl = undefined
    }

    renderPreviewState(form, { status: 'idle' })
  }

  const setBusy = (busy: boolean): void => {
    form.setAttribute('aria-busy', String(busy))
    form.setAttribute('aria-disabled', String(busy || disconnected))
    form.inert = busy || disconnected

    forEachSubmitButton(form, (button) => {
      if (busy) {
        if (button.dataset.labelWasDisabled === undefined) {
          button.dataset.labelWasDisabled = String(button.disabled)
        }

        button.disabled = true
      } else if (!disconnected) {
        button.disabled = button.dataset.labelWasDisabled === 'true'
        delete button.dataset.labelWasDisabled
      }
    })
  }

  const showPrintFallback = (): void => {
    if (objectUrl) {
      setPdfFallback(form, objectUrl)
      setMessage(form, messages.printFallback)
    }
  }

  const printDocument = (content: string): void => {
    const frame = document.createElement('iframe')
    const controller = new AbortController()
    let fallbackTimer: number | undefined
    let printed = false
    let preparing = false
    printFrame = frame
    printController = controller
    frame.hidden = true
    frame.title = messages.frameTitle

    frame.addEventListener(
      'load',
      async () => {
        if (controller.signal.aborted || printed || preparing) {
          return
        }

        const printWindow = frame.contentWindow

        if (!printWindow || typeof printWindow.print !== 'function') {
          showPrintFallback()
          return
        }

        preparing = true

        try {
          await decodePrintImages(printWindow)

          if (controller.signal.aborted || printed) {
            return
          }

          if (fallbackTimer !== undefined) {
            window.clearTimeout(fallbackTimer)
          }

          printWindow.focus()
          printWindow.print()
          printed = true
          setPdfFallback(form)
          setMessage(form)
        } catch {
          showPrintFallback()
        } finally {
          preparing = false
        }
      },
      { signal: controller.signal }
    )

    fallbackTimer = window.setTimeout(showPrintFallback, 5_000)
    rememberTimer(fallbackTimer)
    rememberTimer(window.setTimeout(clearArtifacts, 60_000))
    frame.srcdoc = content
    document.body.append(frame)
  }

  const fetchRenderedPreview = async (attempt = 0): Promise<void> => {
    const endpoint = previewUrlFrom(form)

    if (!endpoint || disconnected || requestController) {
      return
    }

    const controller = new AbortController()
    const body = new FormData(form)
    body.delete('item_ids[]')
    previewController = controller

    try {
      const response = await fetch(endpoint, {
        body,
        credentials: 'same-origin',
        method: 'POST',
        signal: controller.signal
      })

      if (response.status === 503 && attempt === 0) {
        if (previewController === controller) {
          previewController = undefined
          previewTimer = window.setTimeout(() => {
            previewTimer = undefined
            fetchRenderedPreview(1).catch(() => undefined)
          }, 1_000)
        }

        return
      }

      if (!response.ok) {
        throw new Error(await responseError(response, messages.previewFailed))
      }

      const blob = await response.blob()

      if (previewController !== controller || controller.signal.aborted) {
        return
      }

      const nextUrl = URL.createObjectURL(blob)

      if (previewObjectUrl) {
        URL.revokeObjectURL(previewObjectUrl)
      }

      previewObjectUrl = nextUrl
      renderPreviewState(form, { status: 'ready', url: nextUrl })
    } catch (error: unknown) {
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        renderPreviewState(form, {
          currentUrl: undefined,
          message:
            error instanceof Error ? error.message : messages.previewFailed,
          status: 'error'
        })
      }
    } finally {
      if (previewController === controller) {
        previewController = undefined
      }
    }
  }

  const scheduleRenderedPreview = (): void => {
    cancelPreviewRequest()
    updatePreviewDimensions(form)

    if (!previewUrlFrom(form) || disconnected || requestController) {
      clearRenderedPreview()
      return
    }

    renderPreviewState(form, {
      currentUrl: previewObjectUrl,
      status: 'loading'
    })

    previewTimer = window.setTimeout(() => {
      previewTimer = undefined
      fetchRenderedPreview().catch(() => undefined)
    }, previewDelayFrom(form))
  }

  const fetchPdf = async (
    controller: AbortController,
    body: FormData,
    retriesRemaining = PDF_BUSY_RETRY_LIMIT
  ): Promise<Response> => {
    const request = (): Promise<Response> =>
      fetch(form.action, {
        body,
        credentials: 'same-origin',
        method: 'POST',
        signal: controller.signal
      })

    const response = await request()

    if (response.status !== 503 || retriesRemaining === 0) {
      return response
    }

    await abortableDelay(
      pdfBusyRetryDelayFrom(form, response),
      controller.signal
    )
    return fetchPdf(controller, body, retriesRemaining - 1)
  }

  const handleAction = async (intent: Intent): Promise<void> => {
    if (requestController || activePdfRequests.has(form)) {
      return
    }

    const controller = new AbortController()
    requestController = controller
    activePdfRequests.set(form, controller)

    cancelPreviewRequest()
    clearArtifacts()
    setMessage(form)
    setBusy(true)

    const body = new FormData(form)
    body.set('intent', intent)

    try {
      const response = await fetchPdf(controller, body)

      if (!response.ok) {
        throw new Error(
          await responseError(response, messages.pdfRequestFailed)
        )
      }

      if (intent === 'print') {
        const content = await response.text()
        objectUrl = URL.createObjectURL(
          new Blob([content], { type: 'text/html' })
        )
        printDocument(content)
      } else {
        const blob = await response.blob()
        objectUrl = URL.createObjectURL(blob)
        downloadPdf(objectUrl, fileNameFrom(response))
        rememberTimer(window.setTimeout(clearArtifacts, 0))
      }
    } catch (error: unknown) {
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        setMessage(
          form,
          error instanceof Error ? error.message : messages.generationFailed
        )
      }
    } finally {
      if (activePdfRequests.get(form) === controller) {
        activePdfRequests.delete(form)
      }

      if (requestController === controller) {
        requestController = undefined
        setBusy(false)
      }
    }
  }

  actionRoot.addEventListener(
    'click',
    (event) => {
      const target = event.target
      const button =
        target instanceof HTMLElement
          ? target.closest<HTMLButtonElement>('[data-label-submit]')
          : null

      if (button?.form === form) {
        event.preventDefault()
        handleAction(intentFromSubmitter(button)).catch(() => undefined)
      }
    },
    { signal: lifecycleController.signal }
  )

  form.addEventListener('submit', (event) => event.preventDefault(), {
    signal: lifecycleController.signal
  })

  form.addEventListener('input', scheduleRenderedPreview, {
    signal: lifecycleController.signal
  })
  form.addEventListener('change', scheduleRenderedPreview, {
    signal: lifecycleController.signal
  })

  scheduleRenderedPreview()

  return {
    destroy: () => {
      lifecycleController.abort()
      cancelPdfRequest()
      clearArtifacts()
      clearRenderedPreview()
      disconnected = false
      setBusy(false)
    },
    disconnect: () => {
      disconnected = true
      cancelPdfRequest()
      clearArtifacts()
      clearRenderedPreview()
      setBusy(false)
      forEachSubmitButton(form, (button) => {
        button.dataset.labelDisconnectedWasDisabled = String(button.disabled)
        button.disabled = true
      })
    },
    reconnect: () => {
      disconnected = false
      forEachSubmitButton(form, (button) => {
        button.disabled =
          button.dataset.labelWasDisabled === undefined
            ? button.dataset.labelDisconnectedWasDisabled === 'true'
            : button.dataset.labelWasDisabled === 'true'
        delete button.dataset.labelWasDisabled
        delete button.dataset.labelDisconnectedWasDisabled
      })
      form.setAttribute('aria-disabled', 'false')
      form.inert = false
      scheduleRenderedPreview()
    },
    updated: scheduleRenderedPreview
  }
}

export const ItemLabelPdf = {
  mounted(this: ItemLabelPdfHookContext): void {
    if (this.el instanceof HTMLFormElement) {
      this._itemLabelPdfController?.destroy()
      this._itemLabelPdfController = setupItemLabelPdf(this.el)
    }
  },

  disconnected(this: ItemLabelPdfHookContext): void {
    this._itemLabelPdfController?.disconnect()
  },

  updated(this: ItemLabelPdfHookContext): void {
    this._itemLabelPdfController?.updated()
  },

  reconnected(this: ItemLabelPdfHookContext): void {
    this._itemLabelPdfController?.reconnect()
  },

  destroyed(this: ItemLabelPdfHookContext): void {
    this._itemLabelPdfController?.destroy()
    this._itemLabelPdfController = undefined
  }
}
